import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Ruler, Plus, X, Save, Layers, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoadingRows } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { cn } from '@/lib/utils';
import { inventoryService } from '@/services/inventory';
import type { InventoryItem, MeasurementPoint } from '@/types/inventory';

type Unit = 'in' | 'cm';

/**
 * Pre-loaded so an apparel item's size chart is usable the moment the
 * tab opens — the operator just fills in numbers, no row-by-row adding.
 * Matches the standard shirt measurement set.
 */
const DEFAULT_POINTS: MeasurementPoint[] = [
  { key: 'neck', label: 'Neck' },
  { key: 'shoulder', label: 'Shoulder' },
  { key: 'chest', label: 'Chest' },
  { key: 'sleeve_length', label: 'Sleeve Length' },
  { key: 'full_length', label: 'Full Length' },
];

/** One-click palette for other common garment measurements. */
const COMMON_POINTS: MeasurementPoint[] = [
  { key: 'collar', label: 'Collar' },
  { key: 'across_shoulder', label: 'Across Shoulder' },
  { key: 'bicep', label: 'Bicep' },
  { key: 'armhole', label: 'Armhole' },
  { key: 'cuff', label: 'Cuff' },
  { key: 'waist', label: 'Waist' },
  { key: 'hip', label: 'Hip' },
  { key: 'bottom_hem', label: 'Bottom / Hem' },
  { key: 'front_length', label: 'Front Length' },
  { key: 'back_length', label: 'Back Length' },
  { key: 'inseam', label: 'Inseam' },
  { key: 'outseam', label: 'Outseam' },
  { key: 'thigh', label: 'Thigh' },
  { key: 'knee', label: 'Knee' },
];

const cellKey = (variantId: string, pointKey: string) => `${variantId}|${pointKey}`;

/** Trim trailing zeros — 44.00 → "44", 43.180 → "43.18". */
function trimNum(n: number, dp: number): string {
  return String(Number(n.toFixed(dp)));
}

/** Column label for a size variant — its attribute values, else SKU. */
function variantLabel(v: InventoryItem): string {
  const attrs = v.variantAttributes
    ? Object.values(v.variantAttributes).filter(Boolean)
    : [];
  return attrs.length ? attrs.join(' · ') : v.sku;
}

/**
 * Garment size chart — standard measurement points pre-loaded as rows,
 * size variants as columns. Editable in inches or cm; each cell shows
 * the converted unit inline so both are visible at once. Values are
 * stored in inches (cm is a pure ×2.54 conversion).
 */
export function ItemMeasurementsTab({ item }: { item: InventoryItem }) {
  const qc = useQueryClient();

  const variantsQuery = useQuery({
    queryKey: ['inventory', 'variants', item.id],
    queryFn: () => inventoryService.listVariants(item.id),
  });
  const variants = useMemo(() => variantsQuery.data ?? [], [variantsQuery.data]);

  const [template, setTemplate] = useState<MeasurementPoint[]>([]);
  // Per-cell draft strings in the CURRENT unit — keeps decimal typing
  // ("10.") intact; parsed + converted to inches only on save.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [unit, setUnit] = useState<Unit>('in');
  const [dirty, setDirty] = useState(false);

  // (Re)seed local state when variant data lands or refreshes. A brand-
  // new item (no saved template) is pre-loaded with the standard rows
  // so the grid is immediately fillable.
  useEffect(() => {
    if (!variantsQuery.data) return;
    const d: Record<string, string> = {};
    for (const v of variantsQuery.data) {
      const m = v.measurements ?? {};
      for (const [k, val] of Object.entries(m)) {
        d[cellKey(v.id, k)] = trimNum(Number(val), 3);
      }
    }
    const saved = item.measurementTemplate ?? [];
    setDrafts(d);
    setTemplate(saved.length ? saved : DEFAULT_POINTS);
    setUnit('in');
    // No saved chart yet → defaults are unsaved, so enable Save.
    setDirty(saved.length === 0);
  }, [variantsQuery.data, item.measurementTemplate]);

  const setCell = (variantId: string, pointKey: string, raw: string) => {
    setDrafts((d) => ({ ...d, [cellKey(variantId, pointKey)]: raw }));
    setDirty(true);
  };

  const addPoint = (point: MeasurementPoint) => {
    setTemplate((t) =>
      t.some((p) => p.key === point.key) ? t : [...t, point],
    );
    setDirty(true);
  };

  const addCustomPoint = () => {
    setTemplate((t) => [...t, { key: `m${Date.now().toString(36)}`, label: '' }]);
    setDirty(true);
  };

  const removePoint = (key: string) => {
    setTemplate((t) => t.filter((p) => p.key !== key));
    setDirty(true);
  };

  const renamePoint = (key: string, label: string) => {
    setTemplate((t) => t.map((p) => (p.key === key ? { ...p, label } : p)));
    setDirty(true);
  };

  const resetToDefaults = () => {
    setTemplate(DEFAULT_POINTS);
    setDirty(true);
  };

  const switchUnit = (next: Unit) => {
    if (next === unit) return;
    const factor = next === 'cm' ? 2.54 : 1 / 2.54;
    const dp = next === 'cm' ? 2 : 3;
    setDrafts((d) => {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(d)) {
        const n = parseFloat(v);
        out[k] = v.trim() !== '' && Number.isFinite(n) ? trimNum(n * factor, dp) : v;
      }
      return out;
    });
    setUnit(next);
  };

  const save = useMutation({
    mutationFn: () => {
      const variantsPayload: Record<string, Record<string, number>> = {};
      for (const v of variants) {
        const m: Record<string, number> = {};
        for (const p of template) {
          const raw = drafts[cellKey(v.id, p.key)];
          const n = raw != null && raw.trim() !== '' ? parseFloat(raw) : NaN;
          if (Number.isFinite(n)) {
            m[p.key] = unit === 'cm' ? n / 2.54 : n;
          }
        }
        variantsPayload[v.id] = m;
      }
      return inventoryService.saveMeasurements(item.id, {
        template: template.map((p) => ({
          key: p.key,
          label: p.label.trim() || p.key,
        })),
        variants: variantsPayload,
      });
    },
    onSuccess: () => {
      toast.success('Size chart saved');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['inventory', 'variants', item.id] });
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save'),
  });

  const availableCommon = useMemo(
    () => COMMON_POINTS.filter((c) => !template.some((p) => p.key === c.key)),
    [template],
  );

  if (variantsQuery.isLoading) return <LoadingRows rows={5} cols={5} />;
  if (variantsQuery.error) {
    return (
      <ErrorState error={variantsQuery.error} onRetry={() => variantsQuery.refetch()} />
    );
  }
  if (variants.length === 0) {
    return (
      <EmptyState
        icon={Layers}
        title="No size variants yet"
        description="Add size variants on the Variants tab — the size chart needs sizes to put measurements against."
      />
    );
  }

  const otherUnit: Unit = unit === 'in' ? 'cm' : 'in';

  return (
    <div className="space-y-3">
      {/* Intro + unit toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-muted-foreground">
          Standard measurements are pre-loaded — just fill in the numbers for
          each size. Each cell shows both units.
        </p>
        <div className="inline-flex items-center rounded-md border border-input bg-background p-0.5">
          {(['in', 'cm'] as Unit[]).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => switchUnit(u)}
              className={cn(
                'rounded px-3 py-1 text-[11.5px] font-medium transition-colors',
                unit === u
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {u === 'in' ? 'Inches' : 'Centimetres'}
            </button>
          ))}
        </div>
      </div>

      {template.length === 0 ? (
        <EmptyState
          icon={Ruler}
          title="No measurements"
          description="Re-load the standard set, or add measurement points one at a time."
          action={
            <Button size="sm" onClick={resetToDefaults}>
              <RotateCcw className="h-3.5 w-3.5" />
              Load standard measurements
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border/70">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-muted/50">
                <th className="sticky left-0 z-10 min-w-[180px] bg-muted/50 px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Measurement
                </th>
                {variants.map((v) => (
                  <th
                    key={v.id}
                    className="min-w-[96px] border-l border-border/50 px-2 py-2.5 text-center text-[12px] font-semibold text-foreground"
                  >
                    {variantLabel(v)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {template.map((point) => (
                <tr key={point.key} className="group hover:bg-muted/20">
                  <td className="sticky left-0 z-10 bg-background px-2 py-1.5 group-hover:bg-muted/20">
                    <div className="flex items-center gap-1">
                      <Input
                        value={point.label}
                        onChange={(e) => renamePoint(point.key, e.target.value)}
                        placeholder="Measurement name"
                        className="h-7 text-[12px] font-medium"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                        onClick={() => removePoint(point.key)}
                        title="Remove measurement"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                  {variants.map((v) => {
                    const raw = drafts[cellKey(v.id, point.key)] ?? '';
                    const n = parseFloat(raw);
                    const conv =
                      raw.trim() !== '' && Number.isFinite(n)
                        ? `${trimNum(
                            unit === 'in' ? n * 2.54 : n / 2.54,
                            unit === 'in' ? 2 : 3,
                          )} ${otherUnit}`
                        : '';
                    return (
                      <td
                        key={v.id}
                        className="border-l border-border/50 px-1.5 py-1.5 align-top"
                      >
                        <Input
                          value={raw}
                          onChange={(e) => setCell(v.id, point.key, e.target.value)}
                          inputMode="decimal"
                          placeholder="—"
                          className="h-7 text-center font-mono text-[12px] tabular-nums"
                        />
                        <div className="mt-0.5 h-3 text-center text-[9.5px] tabular-nums text-muted-foreground">
                          {conv}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add measurements + save */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <Plus className="h-3.5 w-3.5" />
                Add measurement
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              {availableCommon.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                    Common measurements
                  </DropdownMenuLabel>
                  {availableCommon.map((c) => (
                    <DropdownMenuItem key={c.key} onClick={() => addPoint(c)}>
                      {c.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={addCustomPoint}>
                <Plus className="h-3.5 w-3.5" />
                Custom measurement…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {template.length > 0 && (
            <Button size="sm" variant="ghost" onClick={resetToDefaults}>
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to standard
            </Button>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
        >
          <Save className="h-3.5 w-3.5" />
          {save.isPending ? 'Saving…' : 'Save chart'}
        </Button>
      </div>
    </div>
  );
}
